/* =========================================================================
   Shared chrome for the progression "moments" (XP tally, level-up).

   Unlike the older overlays (candlePrayer / overlays.ts) these are NOT baked
   into appShellHTML — they are created on demand and removed on dismiss, so a
   round-end moment leaves no DOM behind and the app shell keeps its ownership
   boundaries. Same visual language: absolute dim over the phone .screen, one
   centred card, tap-anywhere (or Esc) to continue.
   ========================================================================= */

export interface Moment {
  /** The overlay root (the dim); the card lives inside `.mo-card`. */
  root: HTMLElement;
  /** Resolves once — on tap, Esc, auto-advance, or an explicit close(). */
  done: Promise<void>;
  /** Dismiss now (idempotent). */
  close(): void;
}

/** Honour the OS "reduce motion" setting — animations become instant states. */
export function prefersReducedMotion(): boolean {
  try {
    return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

function host(): HTMLElement {
  return (document.querySelector('.screen') as HTMLElement | null) || document.body;
}

export interface MomentOpts {
  /** Element id (a previous instance with this id is replaced). */
  id: string;
  /** Root class — its own CSS block (`xp-ov`, `lvup-ov`). */
  cls: string;
  /** Card markup (already escaped by the caller). */
  html: string;
  /** Accessible name for the dialog. */
  label: string;
  /** Auto-advance after N ms (0/undefined = wait for the player). */
  autoMs?: number;
  /** Ignore taps for this long so a stray tap can't skip the moment instantly. */
  guardMs?: number;
}

/** Mount a moment overlay and return its handle. Safe to call with no DOM
    (returns a detached root whose promise resolves immediately). */
export function openMoment(opts: MomentOpts): Moment {
  const root = document.createElement('div');
  root.id = opts.id;
  root.className = opts.cls;
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-modal', 'true');
  root.setAttribute('aria-label', opts.label);
  root.innerHTML = opts.html;

  const prev = document.getElementById(opts.id);
  if (prev && prev.parentNode) prev.parentNode.removeChild(prev);
  host().appendChild(root);

  let settled = false;
  let openedAt = Date.now();
  let resolveDone: () => void = () => {};
  const done = new Promise<void>((res) => {
    resolveDone = res;
  });
  const timers: ReturnType<typeof setTimeout>[] = [];

  function onKey(e: KeyboardEvent): void {
    if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      close();
    }
  }

  function close(): void {
    if (settled) return;
    settled = true;
    for (const t of timers) clearTimeout(t);
    document.removeEventListener('keydown', onKey, true);
    root.classList.add('mo-out');
    const remove = (): void => {
      if (root.parentNode) root.parentNode.removeChild(root);
    };
    if (prefersReducedMotion()) remove();
    else timers.push(setTimeout(remove, 220));
    resolveDone();
  }

  root.addEventListener('click', () => {
    if (Date.now() - openedAt < (opts.guardMs ?? 0)) return; // too soon — ignore
    close();
  });
  document.addEventListener('keydown', onKey, true);
  if (opts.autoMs && opts.autoMs > 0) timers.push(setTimeout(close, opts.autoMs));
  openedAt = Date.now();

  return { root, done, close };
}

/** requestAnimationFrame with a setTimeout fallback (jsdom/tests). */
export function raf(fn: () => void): void {
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => fn());
  else setTimeout(fn, 16);
}
